import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, AreaChart, Area } from 'recharts';
import { Sparkles, Clock, TrendingUp, DollarSign, Plus, Trash2, Edit, Info, Lightbulb, Target, AlertCircle, CheckCircle, AlertTriangle, Calendar } from 'lucide-react';
import { formatNumberES } from '@/lib/utils';

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#11998e', '#38ef7d'];

// Función para generar recomendaciones basadas en los resultados
const generarRecomendaciones = (resultado, productos) => {
  const recomendaciones = [];
  
  if (!resultado || !resultado.exito) return recomendaciones;
  
  const { tiempo_usado_horas, tiempo_disponible_horas, cuenta_explotacion, metricas_productos, detalles } = resultado;
  const tiempo_restante = tiempo_disponible_horas - tiempo_usado_horas;
  const porcentaje_tiempo_usado = (tiempo_usado_horas / tiempo_disponible_horas) * 100;
  
  // 1. Análisis de tiempo
  if (tiempo_usado_horas > tiempo_disponible_horas) {
    const exceso = tiempo_usado_horas - tiempo_disponible_horas;
    recomendaciones.push({
      tipo: 'error',
      icono: AlertCircle,
      titulo: 'Excede tiempo disponible',
      mensaje: `Estás usando ${formatNumberES(exceso, 1)} horas más de las disponibles. Considera reducir unidades de productos menos rentables o aumentar tus horas disponibles.`,
      accion: 'Revisa los productos con menor beneficio por hora'
    });
  } else if (tiempo_restante > tiempo_disponible_horas * 0.2) {
    recomendaciones.push({
      tipo: 'info',
      icono: Info,
      titulo: 'Tiempo disponible sin usar',
      mensaje: `Tienes ${formatNumberES(tiempo_restante, 1)} horas disponibles sin usar (${formatNumberES(porcentaje_tiempo_usado, 1)}% utilizado). Podrías aumentar unidades de productos rentables.`,
      accion: 'Considera aumentar unidades de tus productos más rentables'
    });
  }
  
  // 2. Análisis de rentabilidad por producto
  if (metricas_productos && metricas_productos.length > 0) {
    // Producto más rentable
    const masRentable = metricas_productos.reduce((max, p) => 
      p.beneficio_por_hora > max.beneficio_por_hora ? p : max
    );
    
    // Producto menos rentable (de los que están en el mix)
    const menosRentable = metricas_productos.reduce((min, p) => 
      p.beneficio_por_hora < min.beneficio_por_hora ? p : min
    );
    
    if (masRentable.beneficio_por_hora > menosRentable.beneficio_por_hora * 2) {
      recomendaciones.push({
        tipo: 'warning',
        icono: AlertTriangle,
        titulo: 'Gran diferencia de rentabilidad',
        mensaje: `${masRentable.producto_nombre} genera ${formatNumberES(masRentable.beneficio_por_hora)}€/hora, mientras que ${menosRentable.producto_nombre} solo ${formatNumberES(menosRentable.beneficio_por_hora)}€/hora.`,
        accion: `Considera aumentar unidades de ${masRentable.producto_nombre} y reducir ${menosRentable.producto_nombre}`
      });
    }
    
    // Productos con margen negativo o muy bajo
    const productosBajoMargen = metricas_productos.filter(p => p.margen_porcentaje < 10 && p.margen_porcentaje > 0);
    if (productosBajoMargen.length > 0) {
      recomendaciones.push({
        tipo: 'warning',
        icono: AlertTriangle,
        titulo: 'Productos con margen bajo',
        mensaje: `${productosBajoMargen.map(p => p.producto_nombre).join(', ')} tienen márgenes menores al 10%. Revisa sus costes o precios.`,
        accion: 'Considera aumentar precios o reducir costes variables'
      });
    }
  }
  
  // 3. Análisis de gastos fijos
  if (cuenta_explotacion.gastos_fijos > 0) {
    const ratio_gastos_fijos = (cuenta_explotacion.gastos_fijos / cuenta_explotacion.ingresos_totales) * 100;
    if (ratio_gastos_fijos > 40) {
      recomendaciones.push({
        tipo: 'warning',
        icono: AlertTriangle,
        titulo: 'Gastos fijos elevados',
        mensaje: `Tus gastos fijos representan el ${formatNumberES(ratio_gastos_fijos, 1)}% de tus ingresos. Esto puede limitar tu rentabilidad.`,
        accion: 'Revisa si puedes reducir algunos gastos fijos o aumentar ingresos'
      });
    } else if (ratio_gastos_fijos < 15 && cuenta_explotacion.beneficio_neto > 0) {
      recomendaciones.push({
        tipo: 'success',
        icono: CheckCircle,
        titulo: 'Gastos fijos bien controlados',
        mensaje: `Tus gastos fijos representan solo el ${formatNumberES(ratio_gastos_fijos, 1)}% de tus ingresos. Excelente control de costes.`,
        accion: null
      });
    }
  }
  
  // 4. Productos no utilizados pero rentables
  const productosUsadosIds = new Set(detalles.map(d => d.producto_id));
  const productosNoUsados = productos.filter(p => !productosUsadosIds.has(p.id));
  
  if (productosNoUsados.length > 0) {
    // Calcular rentabilidad estimada de productos no usados
    const productosNoUsadosInfo = productosNoUsados.map(p => {
      const ingresos = p.precio * p.max_participantes;
      let costes = 0;
      p.costes_variables.forEach(costo => {
        if (costo.tipo === "fijo") costes += costo.valor;
        else if (costo.tipo === "por_hora") costes += costo.valor * (p.duracion_minutos / 60);
        else if (costo.tipo === "porcentaje") costes += (ingresos * costo.valor / 100);
      });
      const beneficio = ingresos - costes;
      const tiempo = (p.duracion_minutos + (p.tiempo_preparacion_minutos || 0)) / 60;
      const beneficioPorHora = tiempo > 0 ? beneficio / tiempo : 0;
      return { ...p, beneficioPorHora, beneficio };
    }).filter(p => p.beneficioPorHora > 0)
      .sort((a, b) => b.beneficioPorHora - a.beneficioPorHora);
    
    if (productosNoUsadosInfo.length > 0 && tiempo_restante > 0) {
      const mejorNoUsado = productosNoUsadosInfo[0];
      recomendaciones.push({
        tipo: 'info',
        icono: Lightbulb,
        titulo: 'Producto no utilizado con buena rentabilidad',
        mensaje: `${mejorNoUsado.nombre} podría generar ${formatNumberES(mejorNoUsado.beneficioPorHora)}€/hora pero no está en tu mix actual.`,
        accion: `Considera agregar ${mejorNoUsado.nombre} si tienes tiempo disponible`
      });
    }
  }
  
  // 5. Análisis de margen neto
  if (cuenta_explotacion.margen_neto_porcentaje < 10 && cuenta_explotacion.beneficio_neto > 0) {
    recomendaciones.push({
      tipo: 'warning',
      icono: AlertTriangle,
      titulo: 'Margen neto bajo',
      mensaje: `Tu margen neto es del ${formatNumberES(cuenta_explotacion.margen_neto_porcentaje, 1)}%. Intenta optimizar costes o aumentar precios.`,
      accion: 'Revisa costes variables y gastos fijos'
    });
  } else if (cuenta_explotacion.margen_neto_porcentaje > 30) {
    recomendaciones.push({
      tipo: 'success',
      icono: CheckCircle,
      titulo: 'Excelente margen neto',
      mensaje: `Tu margen neto del ${formatNumberES(cuenta_explotacion.margen_neto_porcentaje, 1)}% es muy saludable. ¡Buen trabajo!`,
      accion: null
    });
  }
  
  return recomendaciones;
};

export const Optimizador = () => {
  const { API, token } = useContext(AuthContext);
  const [config, setConfig] = useState({ periodo: 'mensual', horas_disponibles: 160, gastos_fijos: [] });
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [nuevoGasto, setNuevoGasto] = useState({ 
    nombre: '', 
    valor: 0, 
    meses: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], // Por defecto todos los meses
    iva: 21.0,
    incluye_iva: false,
    dias_pago: 0
  });
  const [editandoGasto, setEditandoGasto] = useState(null);
  const [gastoModalOpen, setGastoModalOpen] = useState(false);
  const [ajustesManual, setAjustesManual] = useState({});
  const [productos, setProductos] = useState([]);
  const [creandoProyeccion, setCreandoProyeccion] = useState(false);
  
  const nombresMeses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  useEffect(() => {
    cargarConfiguracion();
    cargarProductos();
  }, []);

  const cargarConfiguracion = async () => {
    try {
      const response = await axios.get(`${API}/configuracion`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Error al cargar configuración:', error);
    }
  };

  const cargarProductos = async () => {
    try {
      const response = await axios.get(`${API}/productos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductos(response.data);
    } catch (error) {
      console.error('Error al cargar productos:', error);
    }
  };

  const guardarConfiguracion = async () => {
    try {
      // Asegurar que siempre sea mensual
      const configToSave = { ...config, periodo: 'mensual' };
      await axios.put(`${API}/configuracion`, configToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuración actualizada');
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
  };

  const abrirModalGasto = (gasto = null, index = null) => {
    if (gasto) {
      setEditandoGasto(index);
      setNuevoGasto({
        nombre: gasto.nombre || '',
        valor: gasto.valor || 0,
        meses: gasto.meses || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        iva: gasto.iva !== undefined ? gasto.iva : 21.0,
        incluye_iva: gasto.incluye_iva || false,
        dias_pago: gasto.dias_pago !== undefined ? gasto.dias_pago : 0
      });
    } else {
      setEditandoGasto(null);
      setNuevoGasto({ 
        nombre: '', 
        valor: 0, 
        meses: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        iva: 21.0,
        incluye_iva: false,
        dias_pago: 0
      });
    }
    setGastoModalOpen(true);
  };

  const cerrarModalGasto = () => {
    setGastoModalOpen(false);
    setEditandoGasto(null);
    setNuevoGasto({ 
      nombre: '', 
      valor: 0, 
      meses: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      iva: 21.0,
      incluye_iva: false,
      dias_pago: 0
    });
  };

  const toggleMesGasto = (mes) => {
    const mesesActuales = nuevoGasto.meses || [];
    if (mesesActuales.includes(mes)) {
      setNuevoGasto({
        ...nuevoGasto,
        meses: mesesActuales.filter(m => m !== mes)
      });
    } else {
      setNuevoGasto({
        ...nuevoGasto,
        meses: [...mesesActuales, mes].sort((a, b) => a - b)
      });
    }
  };

  const seleccionarTodosMeses = () => {
    setNuevoGasto({
      ...nuevoGasto,
      meses: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
    });
  };

  const deseleccionarTodosMeses = () => {
    setNuevoGasto({
      ...nuevoGasto,
      meses: []
    });
  };

  const guardarGastoFijo = () => {
    if (!nuevoGasto.nombre || nuevoGasto.valor <= 0) {
      toast.error('Completa el nombre y el valor del gasto fijo');
      return;
    }
    if (!nuevoGasto.meses || nuevoGasto.meses.length === 0) {
      toast.error('Selecciona al menos un mes de pago');
      return;
    }

    const gastosActualizados = [...config.gastos_fijos];
    if (editandoGasto !== null) {
      gastosActualizados[editandoGasto] = { ...nuevoGasto };
      toast.success('Gasto fijo actualizado');
    } else {
      gastosActualizados.push({ ...nuevoGasto });
      toast.success('Gasto fijo agregado');
    }
    
    setConfig({
      ...config,
      gastos_fijos: gastosActualizados
    });
    cerrarModalGasto();
  };

  const eliminarGastoFijo = (index) => {
    setConfig({
      ...config,
      gastos_fijos: config.gastos_fijos.filter((_, i) => i !== index)
    });
  };

  const optimizar = async () => {
    setLoading(true);
    try {
      // Primero guardar la configuración actual para asegurar que el backend tenga los valores más recientes
      // Asegurar que siempre sea mensual
      const configToSave = { ...config, periodo: 'mensual' };
      console.log('Guardando configuración antes de optimizar:', {
        periodo: configToSave.periodo,
        horas_disponibles: configToSave.horas_disponibles
      });
      
      await axios.put(`${API}/configuracion`, configToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Pequeña pausa para asegurar que la BD se actualice
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Luego optimizar con la configuración actualizada
      const response = await axios.post(`${API}/optimizar`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Resultado de optimización:', {
        tiempo_disponible: response.data.tiempo_disponible_horas,
        tiempo_usado: response.data.tiempo_usado_horas
      });
      
      if (response.data.exito) {
        setResultado(response.data);
        // Inicializar ajustes manuales con valores óptimos
        setAjustesManual(response.data.unidades_optimas);
        toast.success(response.data.mensaje);
      } else {
        toast.error(response.data.mensaje);
      }
    } catch (error) {
      console.error('Error al optimizar:', error);
      toast.error(error.response?.data?.detail || 'Error al optimizar');
    } finally {
      setLoading(false);
    }
  };

  const recalcular = async (nuevasUnidades) => {
    try {
      const response = await axios.post(`${API}/recalcular`, {
        unidades_ajustadas: nuevasUnidades
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResultado(response.data);
    } catch (error) {
      console.error('Error al recalcular:', error);
    }
  };

  const handleAjusteUnidades = (productoId, valor) => {
    const unidades = parseInt(valor) || 0;
    const nuevasUnidades = { ...ajustesManual, [productoId]: unidades };
    setAjustesManual(nuevasUnidades);
    
    // Validar antes de recalcular
    if (unidades >= 0) {
      recalcular(nuevasUnidades);
    }
  };

  const crearProyeccionDesdeOptimizacion = async () => {
    if (!resultado || !resultado.exito) return;
    
    setCreandoProyeccion(true);
    try {
      // Crear objeto de unidades_por_mes con las unidades optimizadas para todos los meses
      const unidadesPorMes = {};
      const mesesProyectados = 12; // Por defecto 12 meses
      
      Object.keys(ajustesManual).forEach(productoId => {
        const unidades = ajustesManual[productoId] || 0;
        unidadesPorMes[productoId] = Array(mesesProyectados).fill(unidades);
      });

      const fechaInicio = new Date();
      fechaInicio.setDate(1); // Primer día del mes actual

      const datosProyeccion = {
        nombre: `Proyección desde Optimización - ${new Date().toLocaleDateString('es-ES')}`,
        descripcion: 'Proyección creada automáticamente desde el optimizador',
        fecha_inicio: fechaInicio.toISOString(),
        meses_proyectados: mesesProyectados,
        unidades_por_mes: unidadesPorMes
      };

      await axios.post(`${API}/proyecciones`, datosProyeccion, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Proyección creada exitosamente. Puedes verla en la sección de Proyecciones.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear proyección');
    } finally {
      setCreandoProyeccion(false);
    }
  };

  const datosBarras = resultado?.detalles.map(d => ({
    nombre: d.producto_nombre,
    unidades: d.unidades,
    ingresos: d.ingresos,
    beneficio: d.beneficio,
    tiempo: d.tiempo_horas
  })) || [];

  const datosTorta = resultado?.detalles.map(d => ({
    name: d.producto_nombre,
    value: d.ingresos,
    beneficio: d.beneficio
  })) || [];

  const datosBeneficio = resultado?.detalles.map(d => ({
    nombre: d.producto_nombre,
    beneficio: d.beneficio,
    ingresos: d.ingresos
  })) || [];

  const datosTiempo = resultado?.detalles.map(d => ({
    nombre: d.producto_nombre,
    tiempo: d.tiempo_horas,
    unidades: d.unidades
  })) || [];

  // Tooltip personalizado para formatear números
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {typeof entry.value === 'number' && entry.name?.includes('€') 
                ? `${formatNumberES(entry.value)}€`
                : typeof entry.value === 'number' && entry.name?.includes('hora')
                ? `${formatNumberES(entry.value, 1)}h`
                : typeof entry.value === 'number'
                ? formatNumberES(entry.value)
                : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Label personalizado para el gráfico de torta
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return percent > 0.05 ? (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        className="text-xs font-semibold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    ) : null;
  };

  return (
    <Layout>
      <div className="page-header">
        <h1 className="page-title" data-testid="optimizador-title">Optimizador de Mix</h1>
        <p className="page-subtitle">Calcula la combinación óptima de servicios</p>
      </div>

      <div className="page-body">
        <Card className="card mb-6">
          <h2 className="text-xl font-bold mb-4">Configuración</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="space-y-2">
              <Label>Horas Disponibles Mensuales</Label>
              <Input
                data-testid="input-horas"
                type="number"
                step="0.5"
                value={config.horas_disponibles}
                onChange={(e) => setConfig({ ...config, horas_disponibles: parseFloat(e.target.value) })}
                placeholder="Ej: 160 horas/mes"
              />
              <p className="text-sm text-gray-500">Horas totales disponibles por mes</p>
            </div>
            
            {/* Instrucciones */}
            <div className="lg:col-span-2">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <Lightbulb size={16} className="text-yellow-500" />
                      Cómo funciona el optimizador
                    </h3>
                    <ul className="text-sm text-gray-700 space-y-1.5">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>Define tus <strong>horas disponibles mensuales</strong> (tiempo total que puedes dedicar)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>Configura tus <strong>gastos fijos mensuales</strong> (alquiler, servicios, seguros, etc.)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>Configura los <strong>impuestos directos (Modelo 130)</strong> para cálculos precisos</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>El algoritmo calcula la combinación óptima que <strong>maximiza tu beneficio neto</strong> (después de impuestos)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>Puedes ajustar manualmente las unidades y ver el impacto en tiempo real</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>Puedes crear una <strong>proyección</strong> desde el mix optimizado para ver la evolución mes a mes</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t pt-4 mb-6">
            <div className="flex justify-between items-center mb-3">
              <Label className="text-base font-semibold">Gastos Fijos Mensuales</Label>
              <Button type="button" size="sm" variant="outline" onClick={() => abrirModalGasto()} data-testid="btn-agregar-gasto">
                <Plus size={14} className="mr-1" /> Agregar Gasto Fijo
              </Button>
            </div>
            <div className="mt-3">
              {config.gastos_fijos && config.gastos_fijos.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  {config.gastos_fijos.map((gasto, index) => {
                    const mesesSeleccionados = gasto.meses || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
                    const mesesTexto = mesesSeleccionados.length === 12 
                      ? 'Todos los meses' 
                      : mesesSeleccionados.length === 1
                      ? nombresMeses[mesesSeleccionados[0] - 1]
                      : `${mesesSeleccionados.length} meses`;
                    const ivaInfo = gasto.iva !== undefined && gasto.iva > 0
                      ? ` | IVA: ${formatNumberES(gasto.iva)}%`
                      : '';
                    const diasPagoInfo = gasto.dias_pago !== undefined && gasto.dias_pago > 0
                      ? ` | Pago: ${gasto.dias_pago}d`
                      : '';
                    
                    return (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded border" data-testid={`gasto-fijo-${index}`}>
                        <div className="flex-1">
                          <div className="font-medium">{gasto.nombre}</div>
                          <div className="text-sm text-gray-600">
                            {formatNumberES(gasto.valor)}€ | {mesesTexto}{ivaInfo}{diasPagoInfo}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button type="button" size="sm" variant="outline" onClick={() => abrirModalGasto(gasto, index)}>
                            <Edit size={14} />
                          </Button>
                          <Button type="button" size="sm" variant="destructive" onClick={() => eliminarGastoFijo(index)} data-testid={`btn-eliminar-gasto-${index}`}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 mb-4">No hay gastos fijos configurados. Haz clic en "Agregar Gasto Fijo" para agregar uno.</p>
              )}
            </div>
          </div>

          {/* Configuración de Impuestos Directos (Modelo 130) */}
          <div className="border-t pt-4 mb-6">
            <Label className="text-base font-semibold mb-3 block">Impuestos Directos (Modelo 130)</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Porcentaje de Impuestos Directos (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={config.porcentaje_modelo130 || 0}
                  onChange={(e) => setConfig({ ...config, porcentaje_modelo130: parseFloat(e.target.value) || 0 })}
                  placeholder="Ej: 15"
                />
                <p className="text-sm text-gray-500">Porcentaje sobre el beneficio antes de impuestos</p>
              </div>
              <div className="space-y-2">
                <Label>Liquidación</Label>
                <Select 
                  value={config.modelo130_trimestral ? 'trimestral' : 'anual'} 
                  onValueChange={(value) => setConfig({ ...config, modelo130_trimestral: value === 'trimestral' ? 1 : 0 })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona frecuencia" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trimestral">Trimestral</SelectItem>
                    <SelectItem value="anual">Anual</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-500">Frecuencia de liquidación del Modelo 130</p>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={guardarConfiguracion} variant="outline" data-testid="btn-guardar-config">Guardar Configuración</Button>
            <Button onClick={optimizar} disabled={loading} data-testid="btn-optimizar">
              <Sparkles size={18} /> {loading ? 'Optimizando...' : 'Optimizar'}
            </Button>
          </div>
        </Card>

        {resultado && resultado.exito && (
          <>
            <div className="stat-grid mb-6">
              <Card className="stat-card" data-testid="resultado-beneficio">
                <div className="stat-icon icon-primary">
                  <DollarSign size={24} />
                </div>
                <div className="stat-label">Beneficio Neto</div>
                <div className="stat-value">{formatNumberES(resultado.beneficio_total)}€</div>
              </Card>

              <Card className="stat-card" data-testid="resultado-tiempo">
                <div className="stat-icon icon-success">
                  <Clock size={24} />
                </div>
                <div className="stat-label">Tiempo Usado</div>
                <div className="stat-value">{resultado.tiempo_usado_horas.toFixed(1)}h</div>
                <p className="text-sm text-gray-500 mt-2">de {resultado.tiempo_disponible_horas}h</p>
              </Card>

              <Card className="stat-card" data-testid="resultado-productos">
                <div className="stat-icon icon-warning">
                  <TrendingUp size={24} />
                </div>
                <div className="stat-label">Productos Activos</div>
                <div className="stat-value">{resultado.detalles.length}</div>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <Card className="card" data-testid="grafico-barras">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <TrendingUp size={20} className="text-purple-600" />
                  Unidades Recomendadas
                </h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={datosBarras} margin={{ top: 10, right: 20, left: 10, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="nombre" 
                      tick={{ fontSize: 11, angle: 0 }}
                      interval={0}
                      height={40}
                      tickFormatter={(value) => value.length > 15 ? `${value.substring(0, 15)}...` : value}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ paddingTop: '10px' }} />
                    <Bar 
                      dataKey="unidades" 
                      fill="#667eea" 
                      radius={[8, 8, 0, 0]}
                      name="Unidades"
                      animationDuration={1000}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Card>

              <Card className="card" data-testid="grafico-torta">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <DollarSign size={20} className="text-green-600" />
                  Distribución de Ingresos
                </h3>
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={datosTorta}
                      cx="50%"
                      cy="45%"
                      labelLine={false}
                      label={renderCustomLabel}
                      outerRadius={130}
                      fill="#8884d8"
                      dataKey="value"
                      animationBegin={0}
                      animationDuration={800}
                    >
                      {datosTorta.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={COLORS[index % COLORS.length]}
                          stroke="#fff"
                          strokeWidth={2}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      content={<CustomTooltip />}
                      formatter={(value) => `${formatNumberES(value)}€`}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      formatter={(value, entry) => (
                        <span style={{ color: entry.color }}>{value}</span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </div>

            {/* Nuevos gráficos adicionales */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <Card className="card">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <DollarSign size={20} className="text-green-600" />
                  Beneficio por Producto
                </h3>
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={datosBeneficio} margin={{ top: 10, right: 20, left: 10, bottom: 40 }}>
                    <defs>
                      <linearGradient id="colorBeneficio" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="nombre" 
                      tick={{ fontSize: 11, angle: 0 }}
                      interval={0}
                      height={40}
                      tickFormatter={(value) => value.length > 15 ? `${value.substring(0, 15)}...` : value}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area 
                      type="monotone" 
                      dataKey="beneficio" 
                      stroke="#10b981" 
                      fillOpacity={1} 
                      fill="url(#colorBeneficio)"
                      name="Beneficio (€)"
                      animationDuration={1000}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>

              <Card className="card">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Clock size={20} className="text-blue-600" />
                  Tiempo por Producto
                </h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={datosTiempo} margin={{ top: 10, right: 20, left: 10, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="nombre" 
                      tick={{ fontSize: 11, angle: 0 }}
                      interval={0}
                      height={40}
                      tickFormatter={(value) => value.length > 15 ? `${value.substring(0, 15)}...` : value}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ paddingTop: '10px' }} />
                    <Bar 
                      dataKey="tiempo" 
                      fill="#3b82f6" 
                      radius={[8, 8, 0, 0]}
                      name="Tiempo (horas)"
                      animationDuration={1000}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </div>

            <Card className="card mb-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold">Ajuste Manual de Unidades</h3>
                  <p className="text-sm text-gray-600 mt-1">Modifica las unidades para ver cómo afecta al resultado en tiempo real</p>
                </div>
                <div className="flex items-center gap-3">
                  {resultado && resultado.exito && (
                    <Button 
                      onClick={crearProyeccionDesdeOptimizacion}
                      disabled={creandoProyeccion}
                      className="flex items-center gap-2"
                      variant="outline"
                    >
                      <Calendar size={18} />
                      {creandoProyeccion ? 'Creando...' : 'Crear Proyección'}
                    </Button>
                  )}
                  {resultado.tiempo_usado_horas > resultado.tiempo_disponible_horas && (
                    <div className="bg-red-50 border border-red-200 rounded px-4 py-2">
                      <p className="text-red-700 font-semibold text-sm">⚠️ Excede tiempo disponible</p>
                      <p className="text-red-600 text-xs">Reduce unidades o aumenta horas</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="tabla-ajustes">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-3 px-4">Producto</th>
                      <th className="text-center py-3 px-4">Unidades (límite)</th>
                      <th className="text-right py-3 px-4">Ingresos</th>
                      <th className="text-right py-3 px-4">Margen</th>
                      <th className="text-right py-3 px-4">Tiempo</th>
                      <th className="text-right py-3 px-4">€/hora</th>
                      <th className="text-right py-3 px-4">Margen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultado.detalles.map((detalle, idx) => {
                      const metrica = resultado.metricas_productos?.find(m => m.producto_id === detalle.producto_id);
                      const unidadesActuales = ajustesManual[detalle.producto_id] || 0;
                      const productoOriginal = productos.find(p => p.id === detalle.producto_id);
                      const limiteMin = productoOriginal?.limite_min || 0;
                      const limiteMax = productoOriginal?.limite_max || 100;
                      
                      return (
                        <tr key={idx} className="border-b hover:bg-gray-50" data-testid={`ajuste-row-${idx}`}>
                          <td className="py-3 px-4">
                            <div className="font-medium">{detalle.producto_nombre}</div>
                          </td>
                          <td className="text-center py-3 px-4">
                            <div className="flex items-center justify-center gap-2">
                              <Input
                                type="number"
                                min="0"
                                value={unidadesActuales}
                                onChange={(e) => handleAjusteUnidades(detalle.producto_id, e.target.value)}
                                className="w-20 text-center"
                                data-testid={`input-unidades-${idx}`}
                              />
                              <span className="text-xs text-gray-500">({limiteMin}-{limiteMax})</span>
                            </div>
                          </td>
                          <td className="text-right py-3 px-4 text-green-600">{formatNumberES(detalle.ingresos)}€</td>
                          <td className="text-right py-3 px-4 font-semibold">{formatNumberES(detalle.beneficio)}€</td>
                          <td className="text-right py-3 px-4">{formatNumberES(detalle.tiempo_horas, 1)}h</td>
                          <td className="text-right py-3 px-4 font-medium text-blue-600">{formatNumberES(metrica?.beneficio_por_hora)}€</td>
                          <td className="text-right py-3 px-4">
                            <span className={`font-semibold ${metrica?.margen_porcentaje > 50 ? 'text-green-600' : metrica?.margen_porcentaje > 30 ? 'text-yellow-600' : 'text-red-600'}`}>
                              {metrica?.margen_porcentaje}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Sugerencias cuando se excede tiempo */}
              {resultado.tiempo_usado_horas > resultado.tiempo_disponible_horas && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="font-semibold text-yellow-900 mb-2">💡 Sugerencias para optimizar:</p>
                  <ul className="text-sm text-yellow-800 space-y-1">
                    <li>• Reduce unidades de productos con menor beneficio/hora</li>
                    <li>• Prioriza productos con mejor margen y rentabilidad</li>
                    <li>• Ajusta las horas disponibles en configuración</li>
                    <li>• Haz clic en "Optimizar" de nuevo para recalcular automáticamente</li>
                  </ul>
                </div>
              )}
            </Card>

            <Card className="card mb-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-bold">Rentabilidad por Producto</h3>
                {resultado.tiempo_usado_horas > resultado.tiempo_disponible_horas && (
                  <div className="bg-red-50 border border-red-200 rounded px-4 py-2">
                    <p className="text-red-700 font-semibold text-sm">⚠️ Excede tiempo disponible</p>
                    <p className="text-red-600 text-xs">Reduce unidades o aumenta horas</p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {resultado.metricas_productos?.map((metrica, idx) => (
                  <div key={idx} className="border rounded-lg p-4 hover:shadow-md transition" data-testid={`metrica-${idx}`}>
                    <h4 className="font-semibold mb-3">{metrica.producto_nombre}</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Margen:</span>
                        <span className="font-semibold">{metrica.margen_porcentaje}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">€/hora:</span>
                        <span className="font-semibold text-blue-600">{formatNumberES(metrica.beneficio_por_hora)}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">% Ingresos:</span>
                        <span className="font-semibold">{metrica.contribucion_ingresos_porcentaje}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">% Beneficio:</span>
                        <span className="font-semibold">{metrica.contribucion_beneficio_porcentaje}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Cuenta de Explotación */}
            <Card className="card mb-6" data-testid="cuenta-explotacion">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold">Cuenta de Explotación Mensual</h3>
                {resultado.tiempo_usado_horas > resultado.tiempo_disponible_horas && (
                  <div className="bg-red-50 border border-red-200 rounded px-4 py-2">
                    <p className="text-red-700 font-semibold text-sm">⚠️ Excede tiempo disponible</p>
                    <p className="text-red-600 text-xs">Reduce unidades o aumenta horas</p>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="font-medium">Ingresos Totales</span>
                  <span className="text-lg font-bold text-green-600">{formatNumberES(resultado.cuenta_explotacion.ingresos_totales)}€</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">- Costes Variables</span>
                  <span className="text-red-600">-{formatNumberES(resultado.cuenta_explotacion.costes_variables)}€</span>
                </div>
                <div className="flex justify-between items-center py-2 bg-gray-50 px-3 rounded font-semibold">
                  <span>= Beneficio Bruto</span>
                  <span>{formatNumberES(resultado.cuenta_explotacion.beneficio_bruto)}€ ({formatNumberES(resultado.cuenta_explotacion.margen_bruto_porcentaje, 1)}%)</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">- Gastos Fijos</span>
                  <span className="text-red-600">-{formatNumberES(resultado.cuenta_explotacion.gastos_fijos)}€</span>
                </div>
                {resultado.cuenta_explotacion.iva_no_deducible !== undefined && resultado.cuenta_explotacion.iva_no_deducible > 0 && (
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600">- Otros impuestos (IVA no deducible)</span>
                    <span className="text-red-600">-{formatNumberES(resultado.cuenta_explotacion.iva_no_deducible)}€</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-2 bg-blue-50 px-3 rounded font-semibold">
                  <span>= Beneficio Antes de Impuestos</span>
                  <span>{formatNumberES(resultado.cuenta_explotacion.beneficio_antes_impuestos || resultado.cuenta_explotacion.beneficio_neto)}€</span>
                </div>
                {resultado.cuenta_explotacion.impuestos_directos !== undefined && resultado.cuenta_explotacion.impuestos_directos > 0 && (
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600">- Impuestos Directos (Modelo 130)</span>
                    <span className="text-red-600">-{formatNumberES(resultado.cuenta_explotacion.impuestos_directos)}€</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-3 bg-gradient-to-r from-purple-50 to-blue-50 px-3 rounded-lg border-2 border-purple-200">
                  <span className="text-lg font-bold">= Beneficio Neto (después de impuestos)</span>
                  <span className="text-2xl font-bold text-purple-700">{formatNumberES(resultado.cuenta_explotacion.beneficio_neto_despues_impuestos || resultado.cuenta_explotacion.beneficio_neto)}€ ({formatNumberES(resultado.cuenta_explotacion.margen_neto_porcentaje, 1)}%)</span>
                </div>
                {/* Información de IVA */}
                {(resultado.cuenta_explotacion.iva_repercutido !== undefined || resultado.cuenta_explotacion.iva_deducible !== undefined || resultado.cuenta_explotacion.iva_no_deducible !== undefined || resultado.cuenta_explotacion.iva_a_pagar !== undefined) && (
                  <div className="border-t pt-3 mt-3 space-y-2">
                    <div className="text-xs font-semibold text-gray-600 mb-2">Información de IVA:</div>
                    {resultado.cuenta_explotacion.iva_repercutido !== undefined && resultado.cuenta_explotacion.iva_repercutido > 0 && (
                      <div className="flex justify-between items-center py-1">
                        <span className="text-xs text-gray-500">IVA Repercutido:</span>
                        <span className="text-xs font-medium text-blue-600">{formatNumberES(resultado.cuenta_explotacion.iva_repercutido)}€</span>
                      </div>
                    )}
                    {resultado.cuenta_explotacion.iva_deducible !== undefined && resultado.cuenta_explotacion.iva_deducible > 0 && (
                      <div className="flex justify-between items-center py-1">
                        <span className="text-xs text-gray-500">IVA Deducible:</span>
                        <span className="text-xs font-medium text-green-600">{formatNumberES(resultado.cuenta_explotacion.iva_deducible)}€</span>
                      </div>
                    )}
                    {resultado.cuenta_explotacion.iva_no_deducible !== undefined && resultado.cuenta_explotacion.iva_no_deducible > 0 && (
                      <div className="flex justify-between items-center py-1">
                        <span className="text-xs text-gray-500">IVA No Deducible:</span>
                        <span className="text-xs font-medium text-orange-600">{formatNumberES(resultado.cuenta_explotacion.iva_no_deducible)}€</span>
                      </div>
                    )}
                    {resultado.cuenta_explotacion.iva_a_pagar !== undefined && (
                      <div className="flex justify-between items-center py-1">
                        <span className="text-xs text-gray-500">IVA a pagar a Hacienda <span className="text-xs">(Ya excluido del beneficio neto)</span>:</span>
                        <span className="text-xs font-medium">{formatNumberES(resultado.cuenta_explotacion.iva_a_pagar)}€</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>

            {/* Recomendaciones */}
            {(() => {
              const recomendaciones = generarRecomendaciones(resultado, productos);
              if (recomendaciones.length === 0) return null;
              
              return (
                <Card className="card mb-6" data-testid="recomendaciones">
                  <div className="flex items-center gap-2 mb-4">
                    <Lightbulb className="text-yellow-500" size={24} />
                    <h3 className="text-xl font-bold">Recomendaciones</h3>
                  </div>
                  <div className="space-y-3">
                    {recomendaciones.map((rec, index) => {
                      const Icono = rec.icono;
                      const bgColor = rec.tipo === 'error' ? 'bg-red-50 border-red-200' 
                        : rec.tipo === 'warning' ? 'bg-yellow-50 border-yellow-200'
                        : rec.tipo === 'success' ? 'bg-green-50 border-green-200'
                        : 'bg-blue-50 border-blue-200';
                      const iconColor = rec.tipo === 'error' ? 'text-red-600' 
                        : rec.tipo === 'warning' ? 'text-yellow-600'
                        : rec.tipo === 'success' ? 'text-green-600'
                        : 'text-blue-600';
                      const textColor = rec.tipo === 'error' ? 'text-red-800' 
                        : rec.tipo === 'warning' ? 'text-yellow-800'
                        : rec.tipo === 'success' ? 'text-green-800'
                        : 'text-blue-800';
                      
                      return (
                        <div key={index} className={`p-4 rounded-lg border ${bgColor}`}>
                          <div className="flex items-start gap-3">
                            <Icono className={`${iconColor} mt-0.5 flex-shrink-0`} size={20} />
                            <div className="flex-1">
                              <h4 className={`font-semibold mb-1 ${textColor}`}>{rec.titulo}</h4>
                              <p className={`text-sm mb-2 ${textColor.replace('800', '700')}`}>{rec.mensaje}</p>
                              {rec.accion && (
                                <p className={`text-xs font-medium ${textColor.replace('800', '600')}`}>
                                  💡 {rec.accion}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })()}
          </>
        )}

        {!resultado && (
          <Card className="card text-center py-12" data-testid="optimizador-empty">
            <Sparkles size={48} className="mx-auto text-purple-400 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Listo para Optimizar</h3>
            <p className="text-gray-600 mb-4">
              Configura tus horas disponibles y haz clic en "Optimizar" para calcular el mix óptimo
            </p>
          </Card>
        )}
      </div>

      {/* Modal para agregar/editar gasto fijo */}
      <Dialog open={gastoModalOpen} onOpenChange={setGastoModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editandoGasto !== null ? 'Editar Gasto Fijo' : 'Agregar Gasto Fijo'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre del Gasto</Label>
                <Input
                  placeholder="Ej: Alquiler, Servicios, Seguro..."
                  value={nuevoGasto.nombre}
                  onChange={(e) => setNuevoGasto({ ...nuevoGasto, nombre: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Valor (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={nuevoGasto.valor}
                  onChange={(e) => setNuevoGasto({ ...nuevoGasto, valor: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Meses de Pago</Label>
              <p className="text-sm text-gray-500 mb-2">
                Selecciona los meses en los que se realiza este pago. Si se paga en todos los meses, selecciona todos.
              </p>
              <div className="flex gap-2 mb-2">
                <Button type="button" size="sm" variant="outline" onClick={seleccionarTodosMeses}>
                  Seleccionar Todos
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={deseleccionarTodosMeses}>
                  Deseleccionar Todos
                </Button>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2 border rounded-lg p-3 bg-gray-50">
                {nombresMeses.map((mes, index) => {
                  const mesNum = index + 1;
                  const estaSeleccionado = nuevoGasto.meses.includes(mesNum);
                  return (
                    <div key={mesNum} className="flex items-center space-x-2">
                      <Checkbox
                        id={`mes-${mesNum}`}
                        checked={estaSeleccionado}
                        onCheckedChange={() => toggleMesGasto(mesNum)}
                      />
                      <Label htmlFor={`mes-${mesNum}`} className="text-sm cursor-pointer">
                        {mes}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>IVA (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={nuevoGasto.iva !== undefined && nuevoGasto.iva !== null ? nuevoGasto.iva : 21.0}
                  onChange={(e) => {
                    const valor = e.target.value === '' ? 0 : parseFloat(e.target.value);
                    setNuevoGasto({ ...nuevoGasto, iva: isNaN(valor) ? 0 : valor });
                  }}
                  placeholder="21"
                />
                <p className="text-xs text-gray-500">0% = exento</p>
              </div>
              <div className="space-y-2">
                <Label>Días hasta Pago</Label>
                <Input
                  type="number"
                  min="0"
                  value={nuevoGasto.dias_pago !== undefined && nuevoGasto.dias_pago !== null ? nuevoGasto.dias_pago : 0}
                  onChange={(e) => {
                    const valor = e.target.value === '' ? 0 : parseInt(e.target.value);
                    setNuevoGasto({ ...nuevoGasto, dias_pago: isNaN(valor) ? 0 : valor });
                  }}
                  placeholder="0"
                />
                <p className="text-xs text-gray-500">0 = inmediato</p>
              </div>
              <div className="space-y-2 flex items-end">
                <div className="flex items-center space-x-2 h-10">
                  <Checkbox
                    id="incluye-iva"
                    checked={nuevoGasto.incluye_iva || false}
                    onCheckedChange={(checked) => setNuevoGasto({ ...nuevoGasto, incluye_iva: checked })}
                  />
                  <Label htmlFor="incluye-iva" className="text-sm cursor-pointer">
                    El valor incluye IVA
                  </Label>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={cerrarModalGasto}>
              Cancelar
            </Button>
            <Button type="button" onClick={guardarGastoFijo}>
              {editandoGasto !== null ? 'Actualizar' : 'Agregar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Optimizador;
